// 3rd party
import * as React from "react";
import { useState } from "react";
import { Maybe } from "tsmonad";
import { Drawer, Button, Tooltip } from "@material-ui/core";
import {
	Menu as DrawerCollapsedIcon,
	DoubleArrow as DrawerOpenIcon,
} from "@material-ui/icons";
import clsx from "clsx";
import * as immutable from "immutable";

// Components
import { ModuleList } from "../components/moduleList";
import { UserArea } from "../components/userArea";
import { ModuleContainer } from "../components/moduleContainer";

// Domain
import * as domain from "@src/domain";

// Styles
import { useHomepageStyles } from "./styles";

// Assets
import defaultLogo from "@src/presentation/homepage/assets/default-logo.png";
import smallLogo from "./assets/smallLogo.png";

export { Homepage };

interface HomepageProps {
	selectedModule: Maybe<domain.Module>;
	config: domain.Config;
	user: domain.User;
	quantities: domain.ProductQuantities;
	productInfo: domain.ProductInfo;
	expandedGroups: immutable.Set<domain.Group["name"]>;
	onLogout: () => void;
	onGroupClick: (group: domain.Group) => void;
}

// Homepage with sidebar and iframe
function Homepage({
	selectedModule,
	config,
	user,
	quantities,
	productInfo,
	expandedGroups,
	onLogout,
	onGroupClick,
}: HomepageProps): JSX.Element {
	const classes = useHomepageStyles();
	const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);

	const handleDrawerChange = (): void => {
		setIsSidebarOpen(!isSidebarOpen);
	};

	return (
		<div className={classes.homePage}>
			<Drawer
				variant="permanent"
				className={clsx(classes.sidebar, {
					[classes.sidebarOpen]: isSidebarOpen,
					[classes.sidebarClose]: !isSidebarOpen,
				})}
				classes={{
					paper: clsx({
						[classes.sidebarOpen]: isSidebarOpen,
						[classes.sidebarClose]: !isSidebarOpen,
					}),
				}}
			>
				<div className={classes.logoContainer}>
					<img
						className={classes.logoImage}
						src={isSidebarOpen ? defaultLogo : smallLogo}
						alt="Logo"
					/>
				</div>
				<ModuleList
					selectedModule={selectedModule}
					menu={config.menu}
					onGroupClick={onGroupClick}
					expandedGroups={expandedGroups}
					isDrawerOpen={isSidebarOpen}
				/>
				<UserArea
					user={user}
					quantities={quantities}
					onLogout={onLogout}
					isDrawerOpen={isSidebarOpen}
				/>
				<div className={classes.line} />
				{isSidebarOpen ? (
					<Button className={classes.drawerButton} onClick={handleDrawerChange}>
						<DrawerOpenIcon className={classes.collapseIcon} />
						<span className={classes.collapseText}>Collapse sidebar</span>
					</Button>
				) : (
					<Tooltip title="Close Drawer" placement="right">
						<Button
							className={classes.drawerButton}
							onClick={handleDrawerChange}
						>
							<DrawerCollapsedIcon className={classes.collapsedIcon} />
						</Button>
					</Tooltip>
				)}
			</Drawer>
			<div className={classes.iframeContainer}>
				<ModuleContainer
					maybeModule={selectedModule}
					productInfo={productInfo}
				/>
			</div>
		</div>
	);
}
